import { useAllCarts } from "@/api/cache/cartQueryOptions";

import {
  AnalyticsTrackingEvent,
  CheckoutDrawer,
  ShoppingCartItem,
  UserRetailerShoppingCart,
  brandclubStyles,
} from "@brandclub/common-ui";
import { Drawer } from "@mui/material";
import { useEffect } from "react";

import { useCartUpdates } from "@/api/cache/cartQueryOptions";
import { createShareableShoppingCart } from "@/api/rest/checkout";
import { StoreBrandingType } from "@/redux/types";
import { create } from "zustand";
import { useTrackActions } from "../../hooks/useTracking";
import { useBCRouting } from "../../providers/BCRoutingContextProvider";
import { HOST_DOMAIN } from "../../utils/domain";
import { CartProductLoading } from "./CartProduct";
import { useSplitTreatments } from "@splitsoftware/splitio-react";
import { useUserProfile } from "@/hooks/useUserProfile";

interface DialogState {
  open: boolean;
  close: () => void;
}

const useDialogStore = create<DialogState>((set) => ({
  open: false,
  close: () => set({ open: false }),
}));

const BagDrawer = () => {
  const { data: userProfile } = useUserProfile();
  const { treatments } = useSplitTreatments({
    names: ["brandclub_checkout_free_shipping_offer"],
    attributes: {
      email: userProfile?.email ?? "",
    },
  });
  const bcRouting = useBCRouting();
  const { open, close } = useDialogStore();
  const { data: allCarts, isLoading: initializingCart } = useAllCarts({
    select: (data) =>
      data.userRetailerShoppingCarts.filter(
        (c) => !!c.items?.length && !!c.retailerId
      ),
    // prevent race conditions when adding an item to the cart triggered this component to mount
    // a premature refetch could cause the new updated cart to be overwritten with stale data
    refetchOnMount: false,
    enabled: false, // Prevents fetching
    staleTime: Infinity, // Prevents background refetches
  });

  const showFreeShippingOffer =
    treatments?.brandclub_checkout_free_shipping_offer?.treatment === "on";

  const { data: cartProducts } = useAllCarts({
    select: (data) => data.products,
    // only read from cache, don't refetch to prevent race conditions when adding an item to the cart
    enabled: false,
    staleTime: Infinity,
  });

  const { data: cartItems } = useAllCarts({
    select: (data) => data.cartItems,
    // only read from cache, don't refetch to prevent race conditions when adding an item to the cart
    enabled: false,
    staleTime: Infinity,
  });
  const { mutate: updateCart } = useCartUpdates();

  const [trackCheckoutBarAction] = useTrackActions();

  const onViewBag = async () => {
    trackCheckoutBarAction(AnalyticsTrackingEvent.VIEW_BAG, {});

    bcRouting.navigate(HOST_DOMAIN + "/mybag?action=checkout");
    close();
  };

  const onCheckout = async (allCarts: UserRetailerShoppingCart[]) => {
    const retailerIds = allCarts?.map((cart) => cart.retailerId);

    trackCheckoutBarAction(AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT, {
      cartItems: allCarts?.map((cart) => ({
        items: cart.items,
        retailerId: cart.retailerId,
      })),
      retailerIds,
      retailerId: retailerIds?.[0],
      source: "brandclub_dtc_checkout_bar",
    });
    const { shoppingCartId } = await createShareableShoppingCart({
      retailerId: allCarts?.[0]?.retailerId as number,
      items: allCarts?.[0]?.items as ShoppingCartItem[],
    });

    bcRouting.location.href =
      HOST_DOMAIN +
      `/checkout?action=checkout&shareShoppingCartId=${shoppingCartId}`;
  };

  const handleAddQuantity = (product: ShoppingCartItem) => {
    updateCart({
      action: "add",
      items: [{ ...product, quantity: 1 }],
      retailerId: product.retailerId,
    });
    trackCheckoutBarAction(AnalyticsTrackingEvent.DTC_UPDATE_ITEM_QUANTITY, {
      stacklineSku: product.stacklineSku,
      retailerId: product.retailerId,
      quantity: product.quantity,
      source: "brandclub_dtc_checkout_bar",
    });
  };

  const handleMinusQuantity = (product: ShoppingCartItem) => {
    if (product.quantity - 1 <= 0) {
      onRemove(product);
      return;
    }
    updateCart({
      action: "add",
      items: [{ ...product, quantity: -1 }],
      retailerId: product.retailerId,
    });
    trackCheckoutBarAction(AnalyticsTrackingEvent.DTC_UPDATE_ITEM_QUANTITY, {
      stacklineSku: product.stacklineSku,
      retailerId: product.retailerId,
      quantity: product.quantity,
      source: "brandclub_dtc_checkout_bar",
    });
  };

  const onRemove = (product: ShoppingCartItem) => {
    updateCart({
      action: "remove",
      items: [{ ...product, quantity: 0 }],
      retailerId: product.retailerId,
    });
  };

  const onEmptyBagAction = () => {
    bcRouting.navigate("/", "_self");
  };

  useEffect(() => {
    if (open) {
      trackCheckoutBarAction(AnalyticsTrackingEvent.DTC_OPEN_BUYBOX, {
        source: "brandclub_dtc_checkout_bar",
      });
    }
  }, [open]);

  return (
    <Drawer
      id="cart-popover"
      open={open}
      sx={{
        width: 462,
        maxWidth: "100%",
      }}
      PaperProps={{
        sx: {
          boxShadow: "none",
          borderRadius: "0",
          maxWidth: "100%",
          ...brandclubStyles.customScrollbar,
        },
      }}
      anchor="right"
      disableAutoFocus
      disableScrollLock
      disableEnforceFocus
      disableRestoreFocus
      onClose={() => {
        close();
      }}
    >
      <CheckoutDrawer
        showFreeShippingOffer={showFreeShippingOffer}
        storeBrandingType={StoreBrandingType.CustomDTCStore}
        cartItems={cartItems ?? []}
        cartProducts={cartProducts}
        allCarts={allCarts}
        loading={initializingCart}
        closeDrawer={close}
        onCheckout={onCheckout}
        handleAddQuantity={handleAddQuantity}
        handleMinusQuantity={handleMinusQuantity}
        onViewBag={onViewBag}
        onEmptyBagAction={onEmptyBagAction}
        LoadingComponent={() => <CartProductLoading></CartProductLoading>}
      />
    </Drawer>
  );
};

BagDrawer.show = () => {
  useDialogStore.setState({
    open: true,
  });
};

export default BagDrawer;
